import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { buildPath } from './lib/tenant';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
    Container, Header, Loader, Dimmer, Message, Segment, Icon, Form, Button,
} from 'semantic-ui-react';
import { getOrderDetail, initiateTransfer } from './lib/tickets';
import { t } from './locales';

function TransferTicket() {
    const { orderRef } = useParams();
    const navigate = useNavigate();
    const [recipientEmail, setRecipientEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [formError, setFormError] = useState(null);
    const [done, setDone] = useState(false);

    const { data: order, isLoading, isError } = useQuery({
        queryKey: ['order', orderRef],
        queryFn: () => getOrderDetail(orderRef),
    });

    const mutation = useMutation({
        mutationFn: () => initiateTransfer({ orderRef, recipientEmail }),
        onSuccess: () => setDone(true),
        onError: (err) => setFormError(err.message),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError(null);

        if (!recipientEmail.trim()) {
            setFormError(t('transfer.recipientEmailRequired'));
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(recipientEmail)) {
            setFormError(t('transfer.invalidEmail'));
            return;
        }
        if (recipientEmail.toLowerCase() !== confirmEmail.toLowerCase()) {
            setFormError(t('transfer.emailMismatch'));
            return;
        }

        mutation.mutate();
    };

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('transfer.loading')} />
            </Dimmer>
        );
    }

    if (isError || order?.error || order?.status !== 'paid') {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message negative>
                    <Message.Header>{t('transfer.orderNotFoundTitle')}</Message.Header>
                    <p>{t('transfer.orderNotFoundBody')}</p>
                </Message>
                <Link to={buildPath('/my-tickets')}>{t('transfer.backToMyTickets')}</Link>
            </Container>
        );
    }

    if (done) {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message positive icon>
                    <Icon name="paper plane outline" />
                    <Message.Content>
                        <Message.Header>{t('transfer.initiatedTitle')}</Message.Header>
                        <p>
                            {t('transfer.initiatedBody', { email: recipientEmail })}
                        </p>
                    </Message.Content>
                </Message>
                <Button
                    basic
                    icon="arrow left"
                    content={t('transfer.backToMyTickets')}
                    onClick={() => navigate(buildPath('/my-tickets'))}
                />
            </Container>
        );
    }

    return (
        <Container className="contentContainer" style={{ maxWidth: 600 }}>
            <div style={{ marginBottom: '1.5rem' }}>
                <Link to={buildPath(`/my-tickets/${orderRef}`)}>
                    <Icon name="arrow left" />
                    {t('transfer.backToTicket')}
                </Link>
            </div>

            <Header as="h1" className="sectionHeader">{t('transfer.title')}</Header>

            <Segment style={{ borderRadius: 12, background: '#f9f9f9', marginBottom: '1.5rem' }}>
                <p style={{ margin: 0 }}>
                    <strong>{order.event_name}</strong>
                </p>
                <p style={{ margin: '0.3rem 0 0', color: '#555' }}>
                    {order.quantity > 1
                        ? t('transfer.orderSummaryPlural', { orderRef: order.order_ref, quantity: order.quantity })
                        : t('transfer.orderSummarySingular', { orderRef: order.order_ref, quantity: order.quantity })}
                </p>
            </Segment>

            <p style={{ color: '#555', marginBottom: '1.5rem' }}>
                {t('transfer.instructions')}
            </p>

            {formError && (
                <Message negative onDismiss={() => setFormError(null)}>
                    {formError}
                </Message>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Field>
                    <label>{t('transfer.recipientEmailLabel')}</label>
                    <input
                        type="email"
                        placeholder={t('transfer.emailPlaceholder')}
                        value={recipientEmail}
                        onChange={e => setRecipientEmail(e.target.value)}
                        autoComplete="off"
                    />
                </Form.Field>
                <Form.Field>
                    <label>{t('transfer.confirmEmailLabel')}</label>
                    <input
                        type="email"
                        placeholder={t('transfer.confirmEmailPlaceholder')}
                        value={confirmEmail}
                        onChange={e => setConfirmEmail(e.target.value)}
                        autoComplete="off"
                    />
                </Form.Field>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                    <Button
                        type="submit"
                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
                        loading={mutation.isLoading}
                        disabled={mutation.isLoading}
                    >
                        {t('transfer.sendButton')}
                    </Button>
                    <Button
                        basic
                        type="button"
                        onClick={() => navigate(buildPath(`/my-tickets/${orderRef}`))}
                    >
                        {t('transfer.cancelButton')}
                    </Button>
                </div>
            </Form>
        </Container>
    );
}

export default TransferTicket;
